import type { NavigationSection, User } from '../types';

export const ROLES = {
    admin: 'admin',
    user: 'user',
    service: 'service',
    attributeManager: 'attribute_manager',
    exclusionsEditor: 'exclusions_editor',
    diagnosticsViewer: 'diagnostics_viewer',
    integrationsAccess: 'integrations_access',
    categoryEditor: 'category_editor',
    b2bClientsManager: 'b2b_clients_manager',
    shopCompaniesManager: 'shop_companies_manager',
    shopOrdersManager: 'shop_orders_manager'
} as const;

export type Capability =
    | 'attributes.manage'
    | 'exclusions.edit'
    | 'diagnostics.view'
    | 'integrations.access'
    | 'categories.edit'
    | 'b2b_clients.manage'
    | 'shop.companies.manage'
    | 'shop.orders.manage'
    | 'operations.view';

export const hasRole = (user: User | null, role: string): boolean =>
    Boolean(user?.roles?.includes(role));

export const hasCapability = (user: User | null, capability: Capability): boolean => {
    if (!user) return false;
    if (hasRole(user, ROLES.admin)) return true;

    switch (capability) {
        case 'attributes.manage':
            return hasRole(user, ROLES.attributeManager);
        case 'exclusions.edit':
            return hasRole(user, ROLES.exclusionsEditor);
        case 'diagnostics.view':
            return hasRole(user, ROLES.diagnosticsViewer);
        case 'integrations.access':
            return hasRole(user, ROLES.integrationsAccess);
        case 'categories.edit':
            return hasRole(user, ROLES.categoryEditor);
        case 'b2b_clients.manage':
            return hasRole(user, ROLES.b2bClientsManager);
        case 'shop.companies.manage':
            return hasRole(user, ROLES.shopCompaniesManager);
        case 'shop.orders.manage':
            return hasRole(user, ROLES.shopOrdersManager);
        default:
            return false;
    }
};

export const checkSectionAccess = (section: NavigationSection, user: User | null): boolean => {
    if (!user) return false;

    const isAdmin = hasRole(user, ROLES.admin);
    switch (section) {
        case 'integrations':
            return hasCapability(user, 'integrations.access');
        case 'categories':
            return hasCapability(user, 'categories.edit');
        case 'products':
            return isAdmin || hasRole(user, ROLES.user);
        case 'attributes':
            return hasCapability(user, 'attributes.manage');
        case 'b2b-clients':
            return hasCapability(user, 'b2b_clients.manage');
        case 'shop-companies':
            return hasCapability(user, 'shop.companies.manage');
        case 'shop-orders':
            return hasCapability(user, 'shop.orders.manage') || isAdmin || Boolean(user.isShopManager);
        case 'smtp':
            return isAdmin;
        case 'order-lifetime':
            return isAdmin;
        case 'maintenance':
            return isAdmin;
        case 'operations':
            return isAdmin;
        case 'users':
            return isAdmin;
        case 'diagnostics':
            return hasCapability(user, 'diagnostics.view');
        case 'suppliers':
            return isAdmin;
        default:
            return false;
    }
};

export const getAccessDeniedMessage = (section: NavigationSection): { title: string; message: string } => {
    switch (section) {
        case 'maintenance':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимы права администратора.'
            };
        case 'b2b-clients':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимо право управления B2B клиентами.'
            };
        case 'shop-companies':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимо право управления компаниями магазина.'
            };
        case 'shop-orders':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимы право управления заказами магазина или назначение менеджером компании.'
            };
        case 'smtp':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимы права администратора.'
            };
        case 'order-lifetime':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимы права администратора.'
            };
        case 'users':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимы права администратора.'
            };
        case 'diagnostics':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходима роль просмотра диагностики.'
            };
        case 'operations':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к разделу операций. Необходимы права администратора.'
            };
        case 'suppliers':
        case 'clients':
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет прав доступа к этому разделу. Необходимы права администратора.'
            };
        case 'integrations':
            return {
                title: 'Доступ ограничен',
                message: 'Для доступа к интеграциям необходимо быть привязанным к клиенту или иметь права администратора. Обратитесь к администратору для настройки вашего аккаунта.'
            };
        default:
            return {
                title: 'Доступ запрещен',
                message: 'У вас нет доступа к этому разделу.'
            };
    }
};
